import { Moon, Sun } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';

interface ThemeToggleProps {
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

export function ThemeToggle({ className, size = 'md' }: ThemeToggleProps) {
  const { isDarkMode, toggleDarkMode } = useTheme();

  const sizeClasses = {
    sm: 'w-10 h-5',
    md: 'w-12 h-6',
    lg: 'w-16 h-8'
  };

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4'
  };

  const thumbSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <button
      onClick={toggleDarkMode}
      className={cn(
        'relative inline-flex items-center rounded-full border-2 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
        isDarkMode 
          ? 'bg-slate-800 border-slate-600' 
          : 'bg-amber-100 border-amber-200',
        sizeClasses[size],
        className
      )}
      aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {/* Background icons */}
      <div className="absolute inset-0 flex items-center justify-between px-1">
        <Sun className={cn(
          'text-amber-500 transition-opacity duration-200',
          isDarkMode ? 'opacity-40' : 'opacity-100',
          iconSizeClasses[size]
        )} />
        <Moon className={cn(
          'text-slate-300 transition-opacity duration-200',
          isDarkMode ? 'opacity-100' : 'opacity-40',
          iconSizeClasses[size]
        )} />
      </div>
      
      {/* Sliding thumb */}
      <div
        className={cn(
          'relative rounded-full shadow-lg transform transition-transform duration-200 ease-in-out flex items-center justify-center',
          isDarkMode 
            ? 'translate-x-full bg-slate-700 border border-slate-500' 
            : 'translate-x-0 bg-white border border-amber-300',
          thumbSizeClasses[size]
        )}
      >
        {isDarkMode ? (
          <Moon className={cn('text-slate-200', iconSizeClasses[size])} />
        ) : (
          <Sun className={cn('text-amber-600', iconSizeClasses[size])} />
        )}
      </div>
    </button>
  );
}